import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { FeatureFlagKey } from '../../../../feature-flags/feature-flag-key';
import { PointcloudType, ScandataModel } from '../../../../scandata/scandata.models';
import { openInNumark } from '../../../../utils/open-in-numark';
import { optionsTranslations } from './options.translations';

@UntilDestroy()
@Component({
  selector: 'sd-options',
  imports: [ModusTooltipModule, ModusButtonModule, ModusIconModule, MatMenuModule],
  templateUrl: './options.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionsComponent {
  @HostBinding('class') class = 'flex flex-col h-full';

  private readonly translationService = inject(TranslationService);
  private readonly store = inject(Store);
  private readonly featureFlagService =
    inject<FeatureFlagService<FeatureFlagKey>>(FeatureFlagService);

  readonly translations = optionsTranslations();

  readonly scandataModels = input.required<ScandataModel[]>();
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);

  readonly viewIn3dClicked = output();
  readonly editTagsClicked = output();
  readonly downloadClicked = output();
  readonly deleteClicked = output();
  readonly removeAllClicked = output();
  readonly removeModelClicked = output<ScandataModel>();

  readonly enableViewInNumark = toSignal(this.featureFlagService.hasFeature$('viewInNumark'), {
    initialValue: false,
  });

  readonly allowDelete = computed(() => {
    return this.scandataModels().every((model) => model.type !== PointcloudType.MobileMappingRun);
  });

  readonly title = computed(() => {
    return this.translationService.translate(this.translations.title.text, {
      count: this.scandataModels().length,
    });
  });

  viewInNumark() {
    openInNumark(
      this.store,
      this.scandataModels().map((model) => model.id),
    );
  }
}
